import { Preferences } from '@capacitor/preferences';
import { registerPlugin } from '@capacitor/core';
import { api } from './api';
import { API_BASE, isNative, platform } from './platform';

interface NativePushPlugin {
  start(options: {
    apiBase: string;
    token: string;
    deviceName?: string;
  }): Promise<void>;
  stop(): Promise<void>;
  requestPermissions(): Promise<{ notifications: boolean }>;
  consumeOpenPath(): Promise<{ path: string | null }>;
  openExternal(options: { url: string }): Promise<void>;
}

const bridge = registerPlugin<NativePushPlugin>('NativePush');
const ENABLED_KEY = 'heritage.nativePush.enabled';
const TOKEN_KEY = 'heritage.nativePush.token';

export async function isNativePushEnabled(): Promise<boolean> {
  if (!isNative()) return false;
  const stored = await Preferences.get({ key: ENABLED_KEY });
  return stored.value === 'true';
}

async function startWithFreshToken(): Promise<void> {
  const previous = await Preferences.get({ key: TOKEN_KEY });
  const { token } = await api.createNativePushToken(platform());
  await Preferences.set({ key: TOKEN_KEY, value: token });
  await bridge.start({
    apiBase: API_BASE,
    token,
    deviceName: platform(),
  });
  if (previous.value && previous.value !== token) {
    await api.revokeNativePushToken(previous.value).catch(() => undefined);
  }
}

export async function enableNativePush(): Promise<void> {
  if (!isNative()) return;
  const permission = await bridge.requestPermissions();
  if (!permission.notifications) {
    throw new Error('push-permission-denied');
  }
  await startWithFreshToken();
  await Preferences.set({ key: ENABLED_KEY, value: 'true' });
  window.dispatchEvent(new Event('heritage:push-enabled'));
}

export async function disableNativePush(): Promise<void> {
  if (!isNative()) return;
  const stored = await Preferences.get({ key: TOKEN_KEY });
  await bridge.stop().catch(() => undefined);
  if (stored.value) {
    await api.revokeNativePushToken(stored.value).catch(() => undefined);
  }
  await Preferences.remove({ key: ENABLED_KEY });
  await Preferences.remove({ key: TOKEN_KEY });
}

export async function syncNativePush(): Promise<void> {
  if (!isNative()) return;
  if (!(await isNativePushEnabled())) return;
  try {
    await startWithFreshToken();
  } catch {
    // Keep the existing native service running if token refresh failed.
  }
}

export async function openExternal(url: string): Promise<void> {
  if (isNative()) {
    await bridge.openExternal({ url }).catch(() => undefined);
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

export async function consumeNativeOpenPath(): Promise<string | null> {
  if (!isNative()) return null;
  try {
    const result = await bridge.consumeOpenPath();
    return result.path;
  } catch {
    return null;
  }
}
